import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Server, Monitor } from 'lucide-react';
import { ROOTSERVER_TYPES } from '@/data/rootserver';
import { useTranslation } from '@/lib/i18n';
import { SEOHead } from '@/components/SEOHead';

const typeIcons = { linux: Server, windows: Monitor };
const typeColors = {
  linux:   { color: 'text-primary',  bg: 'bg-primary/10' },
  windows: { color: 'text-sky-400',  bg: 'bg-sky-500/10' },
};

export default function RootserverPage() {
  const { t } = useTranslation();
  const benefits = t('rootserver.benefits').split('|');

  return (
    <Layout>
      <SEOHead
        title="Root Server Hosting – Linux & Windows KVM"
        description="Root servers with full root access from €4.95/month. Linux KVM and Windows KVM root servers with AMD EPYC, DDoS protection and prepaid model – no contracts."
        canonical="/rootserver"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Root Server Plans',
          url: 'https://deinserverhost.com/rootserver',
          numberOfItems: 2,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Linux KVM Root Server', url: 'https://deinserverhost.com/rootserver/linux' },
            { '@type': 'ListItem', position: 2, name: 'Windows KVM Root Server', url: 'https://deinserverhost.com/rootserver/windows' },
          ],
        }}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-14"
          >
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              KVM <span className="text-gradient">{t('rootserver.title')}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('rootserver.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {benefits.map((b) => (
                <div key={b} className="flex items-center gap-2 px-4 py-2 rounded-full glass border border-border text-sm">
                  <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                  {b}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Type cards */}
          <div className="grid sm:grid-cols-2 gap-8 max-w-3xl mx-auto mb-16">
            {ROOTSERVER_TYPES.map((rs, index) => {
              const Icon = typeIcons[rs.slug] || Server;
              const { color, bg } = typeColors[rs.slug] || typeColors.linux;
              return (
                <motion.div
                  key={rs.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`rootserver-type-${rs.slug}`}
                >
                  <Link to={`/rootserver/${rs.slug}`} className="group block h-full">
                    <div className="glass rounded-2xl border border-border hover:border-primary/50 transition-all duration-300 p-8 h-full flex flex-col">
                      <div className={`w-14 h-14 rounded-xl ${bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                        <Icon className={`w-7 h-7 ${color}`} />
                      </div>

                      <div className="flex items-center gap-3 mb-3">
                        <h2 className="text-xl font-black group-hover:text-primary transition-colors">
                          {t(`rootserver.${rs.tPrefix}_title`)}
                        </h2>
                        {rs.badgeKey && (
                          <Badge className="bg-primary text-xs">{t(`rootserver.${rs.badgeKey}`)}</Badge>
                        )}
                      </div>

                      <p className="text-sm text-muted-foreground mb-6 leading-relaxed flex-1">
                        {t(`rootserver.${rs.tPrefix}_tagline`)}
                      </p>

                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xs text-muted-foreground">{t('rootserver.from')} </span>
                          <span className="text-3xl font-black text-primary">€{rs.fromPrice}</span>
                          <span className="text-xs text-muted-foreground">/mo</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground group-hover:text-primary transition-colors">
                          {t('vps.configureNow')}
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center p-8 glass rounded-2xl"
          >
            <h2 className="text-2xl font-bold mb-3">{t('rootserver.ctaTitle')}</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">{t('rootserver.ctaDesc')}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/dedicated">
                <Button className="bg-primary hover:bg-primary/90">
                  {t('rootserver.viewDedicated')}
                </Button>
              </Link>
              <a href="https://deinserverhost.de/dedicated-sonderposten" target="_blank" rel="noopener noreferrer">
                <Button variant="outline">
                  {t('rootserver.viewSale')}
                </Button>
              </a>
            </div>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
